'use client';

import useMediaQuery from '@mui/material/useMediaQuery';

import { useTheme } from '@classify/react-core-components/styles';
import { styled } from '@classify/react-core-components/styles';
import { ROUTES } from '@openclassrooms/routing';
import Box from '@classify/react-core-components/Box';
import { useLocalisationConfig } from '@openclassrooms/localisation/LocalisationConfigProvider';
import { useConfig } from '@openclassrooms/utils/ConfigProvider';
import { useI18n } from '@openclassrooms/translations/I18nProvider';

import LangSelector from './LangSelector';

import FooterItemsContainer from './FooterItemsContainer';
import FooterLinkItem from './FooterLinkItem';
import FooterItem from './FooterItem';
import FooterPolicies from './FooterPolicies';
import FooterFollow from './FooterFollow';

import { ContactButton } from '../../../contact/components';

interface Props {
  onChangeLang?: (newLang: string) => void;
}

const Root = styled('footer')(({ theme: { spacing, breakpoints } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(4),

  [breakpoints.up('sm')]: {
    marginTop: spacing(4),
  },
}));

const Grid = styled('div')(({ theme: { spacing, breakpoints } }) => ({
  display: 'grid',

  gridTemplateColumns: '1fr',
  gridTemplateAreas: `
    "support"
    "students"
    "business"
    "openclassrooms"
    "various"
  `,

  [breakpoints.up('sm')]: {
    gap: spacing(4),
    gridTemplateRows: '0fr 1fr',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateAreas: `
      "students       support"
      "students       various"
      "business       various"
      "openclassrooms ."
    `,
  },

  [breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gridTemplateRows: '0fr 1fr',
    gridTemplateAreas: `
      "students business support        various"
      "students business openclassrooms various"
    `,
  },
}));

const GridItem = styled(Box)(({ theme: { spacing, breakpoints } }) => ({
  display: 'flex',
  flexDirection: 'column',
  [breakpoints.up('sm')]: {
    gap: spacing(3),
  },
}));

export default function Footer({
  onChangeLang = (newLang) => window.location.assign(`/${newLang}`),
}: Props) {
  const { i18n, translate } = useI18n();
  const { getLocalisationConfig } = useLocalisationConfig();
  const { getConfig } = useConfig();
  const theme = useTheme();
  const canUseCollapsable = useMediaQuery(theme.breakpoints.down('sm'));
  const canDisplayLanguageSelector = i18n.country !== 'US';

  return (
    <Root>
      <Grid>
        <GridItem sx={{ gridArea: 'students' }}>
          <FooterItemsContainer
            title={translate('footer.students.title')}
            isCollapsable={canUseCollapsable}
          >
            {getLocalisationConfig('apprenticeship.isEnabled') && (
              <FooterLinkItem path={ROUTES.APPRENTICESHIP.INDEX}>
                {translate('footer.apprenticeship.label')}
              </FooterLinkItem>
            )}

            <FooterLinkItem path={ROUTES.PATH.INDEX}>
              {translate('footer.trainings_paths.label')}
            </FooterLinkItem>

            <FooterLinkItem path={ROUTES.COURSES.INDEX}>
              {translate('footer.courses.label')}
            </FooterLinkItem>

            {getLocalisationConfig('funding.isEnabled') && (
              <FooterLinkItem path={ROUTES.FUNDING.INDEX}>
                {translate('footer.students.funding')}
              </FooterLinkItem>
            )}

            <FooterLinkItem path={ROUTES.LEARNING_EXPERIENCE.INDEX}>
              {translate('footer.path_experience.label')}
            </FooterLinkItem>

            {getLocalisationConfig('forum.isEnabled') && (
              <FooterLinkItem rawPath="/forum">{translate('footer.forum.label')}</FooterLinkItem>
            )}

            <FooterLinkItem path={String(getLocalisationConfig('blog.student.url'))}>
              {translate('footer.blog_students.label')}
            </FooterLinkItem>
          </FooterItemsContainer>
        </GridItem>

        <GridItem sx={{ gridArea: 'business' }}>
          <FooterItemsContainer
            title={translate('footer.business.title')}
            isCollapsable={canUseCollapsable}
          >
            <FooterLinkItem path={ROUTES.BUSINESS.INDEX}>
              {translate('footer.employers_solutions.label')}
            </FooterLinkItem>

            {getLocalisationConfig('page.uplift.url') && (
              <FooterLinkItem path={String(getLocalisationConfig('page.uplift.url'))}>
                {translate('footer.uplift.label')}
              </FooterLinkItem>
            )}

            <FooterLinkItem path={ROUTES.BUSINESS.SPARK}>
              {translate('footer.spark.label')}
            </FooterLinkItem>

            <FooterLinkItem path={ROUTES.BUSINESS.BOOST}>
              {translate('footer.boost.label')}
            </FooterLinkItem>

            <FooterLinkItem path={String(getLocalisationConfig('blog.employer.url'))}>
              {translate('footer.blog_business.label')}
            </FooterLinkItem>
          </FooterItemsContainer>
        </GridItem>

        <GridItem sx={{ gridArea: 'openclassrooms' }}>
          <FooterItemsContainer
            title={translate('footer.oc.title')}
            isCollapsable={canUseCollapsable}
          >
            <FooterLinkItem path={ROUTES.ABOUT_US.INDEX}>
              {translate('footer.oc.mission')}
            </FooterLinkItem>

            <FooterLinkItem rawPath={String(getLocalisationConfig('openclassrooms.jobs.url'))}>
              {translate('footer.join_us.label')}
            </FooterLinkItem>

            <FooterLinkItem
              rawPath={String(getLocalisationConfig('openclassrooms.become-mentor.url'))}
            >
              {translate('footer.mentor.label')}
            </FooterLinkItem>

            <FooterLinkItem rawPath="https://coaches.openclassrooms.com">
              {translate('footer.coach.label')}
            </FooterLinkItem>

            <FooterLinkItem rawPath="https://shop.openclassrooms.com">
              {translate('footer.eshop.label')}
            </FooterLinkItem>
          </FooterItemsContainer>
        </GridItem>

        <GridItem sx={{ gridArea: 'support' }}>
          <FooterItemsContainer
            title={`${translate('footer.support.title')}`}
            isCollapsable={false}
          >
            <>
              <ContactButton
                render={({ onClick }) => (
                  <FooterLinkItem path={ROUTES.CONTACT} onClick={onClick}>
                    {translate('global.word.contact_us.action')}
                  </FooterLinkItem>
                )}
              />
              <FooterLinkItem path={String(getLocalisationConfig('faq.student.url'))}>
                {translate('footer.support.faq_students')}
              </FooterLinkItem>
              <FooterLinkItem path={String(getLocalisationConfig('faq.employer.url'))}>
                {translate('footer.support.faq_business')}
              </FooterLinkItem>
              <FooterLinkItem path={String(getLocalisationConfig('faq.mentor.url'))}>
                {translate('footer.support.mentor')}
              </FooterLinkItem>
            </>
          </FooterItemsContainer>
        </GridItem>

        <GridItem sx={{ gridArea: 'various' }}>
          {canDisplayLanguageSelector && (
            <FooterItemsContainer title={translate('footer.language.title')} component="div">
              <FooterItem>
                <LangSelector onChangeLang={onChangeLang} />
              </FooterItem>
            </FooterItemsContainer>
          )}

          <FooterItemsContainer title={translate('footer.follow_us.title')} component="div">
            <FooterItem>
              <FooterFollow />
            </FooterItem>
          </FooterItemsContainer>
        </GridItem>
      </Grid>
      <FooterPolicies />
      <div style={{ display: 'none' }}>{`contact:${getConfig('contactMail')}`}</div>
    </Root>
  );
}
